import { Component, forwardRef, Input } from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  Validator,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { validatorNumberRange } from '@emma-helpers/emma-validators.helper';
import { sectionAnimations } from '@platform/helpers/animations.helper';
import { noop } from 'lodash';

import { EMMAFormElementComponent } from './emma-form-element.component';

const CONTROL_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  // tslint:disable-next-line: no-use-before-declare
  useExisting: forwardRef(() => EMMAUnlimitedOrNumberInputComponent),
  multi: true,
};
const VALIDATOR: any = {
  provide: NG_VALIDATORS,
  // tslint:disable-next-line: no-use-before-declare
  useExisting: forwardRef(() => EMMAUnlimitedOrNumberInputComponent),
  multi: true,
};

@Component({
    selector: 'emma-unlimited-or-number-input',
    templateUrl: './emma-unlimited-or-number-input.component.html',
    providers: [
        CONTROL_VALUE_ACCESSOR,
        VALIDATOR,
        {
            provide: EMMAFormElementComponent,
            useExisting: EMMAUnlimitedOrNumberInputComponent,
        },
    ],
    animations: [...sectionAnimations],
    standalone: false
})
export class EMMAUnlimitedOrNumberInputComponent
  extends EMMAFormElementComponent
  implements ControlValueAccessor, Validator
{
  @Input() unlimitedLabel = $localize`Ilimitados`;
  @Input() valueLabel = $localize`Número máximo`;
  @Input() defaultValue = 1;
  @Input() get value() {
    return this.innerValue;
  }
  set value(rawValue: number | string) {
    const value = Number(rawValue);
    if (value !== this.innerValue) {
      this.setInnerValue(value);
      this.onChangeCallback(value);
      this.$change.emit(value);
    }
  }
  private innerValue = 0;
  private onTouchedCallback: () => void = noop;
  private onChangeCallback: (_: number) => void = noop;

  unlimited = !this.value;

  @Input() min = 0;
  @Input() max = Infinity;
  @Input() step = 1;

  setInnerValue(value: number) {
    this.innerValue = value;
    this.updateUnlimited();
  }

  onBlur() {
    this.onTouchedCallback();
    this.$blur.emit();
    this.updateUnlimited();
  }

  onChangeUnlimited(unlimited: boolean) {
    if (!unlimited) {
      this.value = this.defaultValue;
    } else {
      this.value = 0;
    }
    this.onBlur();
  }

  updateUnlimited() {
    this.unlimited = !this.innerValue;
  }

  // From ControlValueAccessor interface
  writeValue(rawValue: number | string) {
    const value = Number(rawValue);
    if (value !== this.innerValue) {
      this.setInnerValue(value);
    }
  }

  // From ControlValueAccessor interface
  registerOnChange(fn: any) {
    this.onChangeCallback = fn;
  }

  // From ControlValueAccessor interface
  registerOnTouched(fn: any) {
    this.onTouchedCallback = fn;
  }

  // From Validator interface
  override validate: ValidatorFn = (control: AbstractControl) => {
    const validators: ValidatorFn[] = [];
    if (this.required) {
      validators.push(Validators.required);
    }
    if (!this.unlimited) {
      validators.push(validatorNumberRange(this.max, this.min));
    }
    if (this.validateFn) {
      validators.push(this.validateFn);
    }
    if (validators.length) {
      const validator = Validators.compose(validators);
      return validator ? validator(control) : null;
    }

    return null;
  };
}
